import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { SelectComponet, Tables , PermissionsComponent   } from '../../../../Components';
import headerData from './headData';
import useFetchData from '../../../../Hooks/useFetchData';
import {
  BulkUpdateGeneralClientTracking,
  CreateGeneralClientTrackingActivity,
  ExportGeneralClientTrackingReportBulk,
  FilterGeneralClientTracking,
} from '../../../../Services';
import PerformanceDateRangePicker from '../../../../Layouts/Home/PerformanceInsightsReports/components/PerformanceDateRangePicker';
import {
  showError,
  showSuccess,
  sideMenuComponentUpdate,
  sideMenuIsOpenUpdate,
  returnPropsByPermissions 
} from '../../../../Helper';
import GeneralInquirySideCard from './components/GeneralInquirySideCard';
import { TableActions, TableFilterTypesEnum } from '../../../../Enums';
import { bulkActions } from './enums';
import { Button } from '@material-ui/core';
import UpdateInquiryStatusDialog from './components/UpdateInquiryStatusDialog';
import { useKeyGenerator } from '../../../../Hooks/keyGenerator';
import SourceOfLeadBulkUpdate from './components/SourceOfLeadBulkUpdate';
import AddActivityDialog from '../ClientsTrackingUtilities/Components/AddActivityDialog';
import CreateLeadDialog from '../../../../Components/CreateLeadDialog/CreateLeadDialog';
import { ClientsTrackingPermissions } from '../../../../Permissions' ; 

const GeneralInquiry = () => {
  const [pagination, setPagination] = React.useState({
    pageSize: 25,
    pageIndex: 1,
  });

  const [activeAction, setActiveAction] = useState(null);
  const [key, refresh] = useKeyGenerator();
  const [activeItemIndex, setActiveItemIndex] = useState(null);
  const [leadOfSourceDialogOpen, setLeadOfSourceDialogOpen] = useState(false);
  const [filter, setFilter] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [searchDuration, setSearchDuration] = useState({
    fromEnquiryDate: null,
    toEnquiryDate: null,
  });
  const [isOpenCreateLeadDialog, setIsOpenCreateLeadDialog] = useState(false);
  const [openActivitiesDialog, setOpenActivitiesDialog] = useState(false);
  const [openUpdateInquiryStatusDialog, setOpenUpdateInquiryStatusDialog] =
    useState(false);
  const filters = useMemo(
    () => ({ ...pagination, ...searchDuration, ...filter }),
    [pagination, searchDuration, filter]
  );
  const focusedRowChanged = (rowIndex, item) => {
    if (item) {
      sideMenuIsOpenUpdate(true);
      setActiveItemIndex(rowIndex);
    }
  };
  const getIsSelected = (row) =>
    selectedRows.includes(row.generalClientTrackingId);
  const onSelectAllClicked = (e) => {
    setSelectedRows(
      e.target.checked
        ? data.result.map((row) => row.generalClientTrackingId)
        : []
    );
  };
  const getIsSelectedAll = () => selectedRows.length === data.result?.length;
  const onSelectClicked = (row) => {
    setSelectedRows((prevSelectedRows) =>
      prevSelectedRows.includes(row.generalClientTrackingId)
        ? prevSelectedRows.filter((id) => id !== row.generalClientTrackingId)
        : [...prevSelectedRows, row.generalClientTrackingId]
    );
  };

  const onClickBulkAction = async () => {
    if (activeAction === bulkActions.leadStatusUpdate.id) {
      setOpenUpdateInquiryStatusDialog(true);
    } else if (activeAction === bulkActions.leadSourceUpdate.id) {
      setLeadOfSourceDialogOpen(true);
    } else if (activeAction === bulkActions.exportCSV.id) {
      onExportCSV();
    }
  };

  const { data, loading, refetch } = useFetchData(
    FilterGeneralClientTracking,
    filters
  );
  const activeItem = useMemo(
    () => data.result?.[activeItemIndex],
    [activeItemIndex, data]
  );
  const onBulkStatusUpdate = async (targetStatus) => {
    const selectedItems = data.result
      .filter((item) => selectedRows.includes(item.generalClientTrackingId))
      .map((item) => ({
        ...item,
        inquiryStatus: targetStatus,
      }));
    try {
      await BulkUpdateGeneralClientTracking({
        items: selectedItems,
      });
      setActiveAction(null);
      setSelectedRows([]);
      setOpenUpdateInquiryStatusDialog(false);
      await refetch();
      refresh();
    } catch (error) {
      showError(error.message ?? 'Something went wrong, please try again.');
    }
  };

  const onBulkSourceOfLeadUpdate = async (sourceOfLeadId) => {
    const selectedItems = data.result
      .filter((item) => selectedRows.includes(item.generalClientTrackingId))
      .map((item) => ({
        ...item,
        sourceOfLeadId,
      }));
    try {
      await BulkUpdateGeneralClientTracking({
        items: selectedItems,
      });
      setActiveAction(null);
      setSelectedRows([]);
      setLeadOfSourceDialogOpen(false);
      await refetch();
      refresh();
    } catch (error) {
      showError(error.message ?? 'Something went wrong, please try again.');
    }
  };

  const onExportCSV = async () => {
    try {
      await ExportGeneralClientTrackingReportBulk(
        selectedRows.map((id) => ({ generalClientTrackingId: id }))
      );
      showSuccess('CSV exported successfully');
      setActiveAction(null);
      setSelectedRows([]);
    } catch (error) {
      showError('Something went wrong, please try again.');
    }
  };

  const onSaveActivity = async (data) => {
    try {
      data.activityTypeId = data.activityType.activityTypeId;
      await CreateGeneralClientTrackingActivity(data);
      showSuccess('create successfully');
      refresh();
    } catch (error) {
      showError('create error');
    } finally {
      setOpenActivitiesDialog(false);
    }
  };

  const onActionClicked = (enumKey, _item, index) => {
    if (enumKey === TableActions.addActivity.key) {
      setOpenActivitiesDialog(true);
      setActiveItemIndex(index);
    } else if (enumKey === TableActions.createLeadText.key) {
      setActiveItemIndex(index);
      setIsOpenCreateLeadDialog(true);
    }
  };

  const tableHeaderData = useMemo(
    () => headerData(setFilter, refetch),
    [setFilter, refetch]
  );

 const getTableActionsWithPermissions = () => {
   let list = [];
   if (
     returnPropsByPermissions(
       ClientsTrackingPermissions.AddActivityForGeneralInquiry.permissionsId
     )
   )
     list.push({
       enum: TableActions.addActivity.key,
     });
   if (
     returnPropsByPermissions(
       ClientsTrackingPermissions.CreateLeadForGeneralInquiry.permissionsId
     )
   )
     list.push({ enum: TableActions.createLeadText.key });

   return list;
 };

  const getBulkActionssWithPermissions = () => {
    let list = [];
    if (
      returnPropsByPermissions(
        ClientsTrackingPermissions.UpdateLeadSourc.permissionsId
      )
    )
      list.push(bulkActions.leadSourceUpdate);
    if (
      returnPropsByPermissions(
        ClientsTrackingPermissions.UpdateInquiryStatus.permissionsId
      )
    )
      list.push(bulkActions.leadStatusUpdate);

    if (
      returnPropsByPermissions(
        ClientsTrackingPermissions.ExportGeneralInquiry.permissionsId
      )
    )
      list.push(bulkActions.exportCSV);

    return list;
  };

  useEffect(() => {
    if (activeItem) {
      sideMenuComponentUpdate(
        <GeneralInquirySideCard
          key={
            activeItem.generalClientTrackingId +
            activeItem.inquiryStatus +
            activeItem.sourceOfLeadId
          }
          onSave={async () => {
            await refetch();
            refresh();
            sideMenuIsOpenUpdate(false);
            setActiveItemIndex(null);
          }}
          {...activeItem}
        />
      );
    }
    return () => {
      sideMenuComponentUpdate(null);
    };
  }, [
    activeItem?.inquiryStatus,
    activeItem?.sourceOfLeadId,
    activeItem?.generalClientTrackingId,
  ]);

  return (
    <div className='p-3'>
      <div className='flex fj-between'>
        <div className='flex gap-6 fa-center'>
          <SelectComponet
            data={getBulkActionssWithPermissions()}
            wrapperClasses={'w-270px'}
            emptyItem={{ value: null, text: 'Actions', isHiddenOnOpen: true }}
            onSelectChanged={setActiveAction}
            value={activeAction}
            textInput={'name'}
            valueInput={'id'}
          />
          {activeAction && (
            <div>
              <Button
                className='btns theme-solid'
                disabled={selectedRows.length === 0}
                onClick={onClickBulkAction}
              >
                {bulkActions[activeAction]?.name}
              </Button>
            </div>
          )}
        </div>
        <div className='d-flex fj-end fa-end py-3 gap-1'>
          <PerformanceDateRangePicker
            onDateRangeChange={(e) =>
              setSearchDuration({
                fromEnquiryDate: e.startDate,
                toEnquiryDate: e.endDate,
              })
            }
            withoutDiff
          />
        </div>
      </div>
      <Tables
        key={key}
        data={data.result ?? []}
        headerData={tableHeaderData}
        totalItems={data.totalCount ?? 0}
        loading={loading}
        activePage={pagination.pageIndex - 1}
        itemsPerPage={pagination.pageSize}
        onPageIndexChanged={(pageIndex) =>
          setPagination({ ...pagination, pageIndex })
        }
        onPageSizeChanged={(pageSize) =>
          setPagination({ ...pagination, pageSize })
        }
        selectAllOptions={
          activeAction && {
            selectedRows,
            getIsSelected,
            withCheckAll: true,
            onSelectAllClicked,
            isSelectAll: getIsSelectedAll(),
            onSelectClicked,
            disabledRows: [],
          }
        }
        isLoading={loading}
        defaultActions={getTableActionsWithPermissions()}
        actionsOptions={{ onActionClicked }}
        focusedRowChanged={focusedRowChanged}
        isWithFilter
        filterData={tableHeaderData.map((item, i) => ({
          ...item,
          id: i,
          key: i,
          filterType: item.filterType ?? TableFilterTypesEnum.textInput.key,
          component: item.filterComponent,
          searchableKey: item.searchableKey ?? item.input,
        }))}
        onFilterValuesChanged={(e) => {
          if (e && !!Object.values(e).length) {
            const { value, searchableKey } = Object.values(e)[0];
            setFilter((filter) => ({
              ...filter,
              [searchableKey]: value,
            }));
          } else {
            setFilter((filter) => {
              const updatedFilter = Object.entries(filter).filter(
                ([_key, value]) => !!value
              );
              return Object.fromEntries(updatedFilter);
            });
          }
        }}
      />
      <UpdateInquiryStatusDialog
        open={openUpdateInquiryStatusDialog}
        onClose={() => setOpenUpdateInquiryStatusDialog(false)}
        onSave={onBulkStatusUpdate}
      />
      <SourceOfLeadBulkUpdate
        open={leadOfSourceDialogOpen}
        onClose={() => setLeadOfSourceDialogOpen(false)}
        onSave={onBulkSourceOfLeadUpdate}
      />
      {openActivitiesDialog && activeItem && (
        <AddActivityDialog
          generalClientTrackingId={activeItem.generalClientTrackingId}
          open
          contactId={activeItem.contactId}
          onClose={() => setOpenActivitiesDialog(false)}
          onSave={onSaveActivity}
        />
      )}
      <CreateLeadDialog
        open={isOpenCreateLeadDialog}
        setOpen={setIsOpenCreateLeadDialog}
        contactId={activeItem?.contactId}
      />
    </div>
  );
};
export default GeneralInquiry;
